import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/admin-common.css';
import './AlumniSpeak.css';

const AlumniSpeak = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    student_name: '',
    placed_company: '',
    designation: '',
    passout_year: '',
    student_message: '',
    student_photo: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userRole, setUserRole] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchAlumni();
  }, [statusFilter]);

  const fetchAlumni = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE}/api/admin/alumni-speak`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const res = await fetch(url, { headers: authHeaders() });
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}. ${text.substring(0, 80)}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load alumni');
      setAlumni(data.alumni || []);
    } catch (err) {
      setError(err.message || 'Failed to load alumni');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        setError('Image size must be less than 200 KB');
        return;
      }
      setForm((prev) => ({ ...prev, student_photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setForm({
      student_name: '',
      placed_company: '',
      designation: '',
      passout_year: '',
      student_message: '',
      student_photo: null,
    });
    setEditingId(null);
    setPreviewImage(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.student_name) {
      setError('Name is required');
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined) formData.append(k, v);
    });

    try {
      const url = editingId
        ? `${API_BASE}/api/admin/alumni-speak/${editingId}`
        : `${API_BASE}/api/admin/alumni-speak`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: formData,
      });

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save alumni');

      setSuccess(data.message || 'Saved');
      resetForm();
      await fetchAlumni();
    } catch (err) {
      setError(err.message || 'Failed to save alumni');
    }
  };

  const handleEdit = (item) => {
    setForm({
      student_name: item.student_name,
      placed_company: item.placed_company,
      designation: item.designation,
      passout_year: item.passout_year,
      student_message: item.student_message,
      student_photo: null,
    });
    setEditingId(item.id);
    setPreviewImage(item.student_photo ? `${API_BASE}${item.student_photo}` : null);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/alumni-speak/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete');
      setSuccess('Deleted');
      await fetchAlumni();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApprove = async (id) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/alumni-speak/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to approve');
      setSuccess('Approved');
      await fetchAlumni();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this entry?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/alumni-speak/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to reject');
      setSuccess('Rejected');
      await fetchAlumni();
    } catch (err) {
      setError(err.message);
    }
  };



  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/alumni-speak/export/csv`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `alumni_speak_${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
      setSuccess('Exported successfully');
    } catch (err) { setError(err.message); } finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { setError('Please select a CSV file'); return; }
    setImporting(true); setError(''); setSuccess('');
    const formData = new FormData(); formData.append('csv_file', file);
    try {
      const res = await fetch(`${API_BASE}/api/admin/alumni-speak/import/csv`, { method: 'POST', headers: authHeaders(), body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(data.message);
      if (data.errors?.length) setError(`Some rows failed: ${data.errors.join(', ')}`);
      await fetchAlumni();
    } catch (err) { setError(err.message); } finally { setImporting(false); e.target.value = ''; }
  };

  const downloadSample = () => {
    const csv = `student_name,placed_company,designation,passout_year,student_message\nJohn Doe,Maersk Line,Marine Engineer,2023,Great experience at MANET\nJane Smith,Shipping Corp,Naval Architect,2022,Excellent faculty and facilities`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'alumni_speak_sample.csv';
    document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); document.body.removeChild(a);
  };

  return (
    <div className="admin-page alumni-speak-page">
      <div className="page-heading">
        <h1>Alumni Speak</h1>
        <p>Collect alumni testimonials with approval workflow.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="admin-actions">
        <button onClick={handleExport} disabled={exporting} className="btn-secondary">{exporting ? 'Exporting...' : '📥 Export CSV'}</button>
        <label className="btn-secondary" style={{ cursor: 'pointer' }}>
          {importing ? 'Importing...' : '📤 Import CSV'}
          <input type="file" accept=".csv" onChange={handleImport} disabled={importing} style={{ display: 'none' }} />
        </label>
        <button onClick={downloadSample} className="btn-secondary">📋 Download Sample CSV</button>
      </div>

      <div className="admin-grid alumni-speak-grid">
        <div className="card">
          <h2>{editingId ? 'Edit Alumni' : 'Add Alumni'}</h2>
          <form className="admin-form alumni-speak-form" onSubmit={handleSubmit}>
            <label>
              Name *
              <input name="student_name" value={form.student_name} onChange={handleChange} required />
            </label>
            <label>
              Placed Company
              <input name="placed_company" value={form.placed_company} onChange={handleChange} />
            </label>
            <label>
              Designation
              <input name="designation" value={form.designation} onChange={handleChange} placeholder="e.g., Software Engineer" />
            </label>
            <label>
              Passout Year
              <input 
                type="text" 
                name="passout_year" 
                value={form.passout_year} 
                onChange={handleChange} 
                placeholder="e.g., 2023 or 2022-2024"
              />
            </label>
            <label>
              Message (Optional)
              <textarea
                name="student_message"
                value={form.student_message}
                onChange={handleChange}
                rows={4}
                placeholder="Share your experience..."
              />
            </label>
            <label>
              Photo (Max 200 KB)
              <input type="file" accept="image/*" onChange={handleImageChange} />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <p>Will be resized to 200x200</p>
                </div>
              )}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Add'}</button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="card alumni-list-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>Alumni Entries</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px'}}>
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : alumni.length === 0 ? (
            <p>No entries yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alumni.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {a.student_photo ? (
                        <img src={`${API_BASE}${a.student_photo}`} alt={a.student_name} className="alumni-thumb" />
                      ) : (
                        <div className="alumni-thumb-placeholder">No Photo</div>
                      )}
                    </td>
                    <td>{a.student_name}</td>
                    <td>{a.placed_company}</td>
                    <td>{a.passout_year}</td>
                    <td><span className={`status status-${a.status}`}>{a.status}</span></td>
                    <td className="actions">
                      {userRole === 'web_coordinator' && (
                        <>
                          <button className="btn-edit" onClick={() => handleEdit(a)}>✏️ Edit</button>
                          <button className="btn-delete" onClick={() => handleDelete(a.id)}>🗑️ Delete</button>
                        </>
                      )}
                      {userRole === 'superadmin' && (
                        <>
                          <button className="btn-edit" onClick={() => handleEdit(a)}>✏️ Edit</button>
                          <button className="btn-delete" onClick={() => handleDelete(a.id)}>🗑️ Delete</button>
                          {a.status === 'pending' && (
                            <>
                              <button className="btn-approve" onClick={() => handleApprove(a.id)}>✓ Approve</button>
                              <button className="btn-reject" onClick={() => handleReject(a.id)}>✗ Reject</button>
                            </>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlumniSpeak;